import {
  CalendarDays,
  ChevronDown,
  CircleCheck,
  Download,
  Lock,
  LogOut,
  Moon,
  ShieldCheck,
  Sun,
  User,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/paths';
import { useOverlays } from '@/components/Overlays';
import { Avatar } from '@/design-system/ui/Avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/design-system/ui/Menu';
import { usePwa } from '@/pwa/installState';
import { PERSONA_LABEL, useStore } from '@/store/AppStore';
import { THEME_COPY, useTheme } from '@/theme';

/**
 * Menu do usuário, com o perfil do login: troca o tema, oferece instalar o aplicativo
 * ("Aplicativo instalado" depois — abre as instruções de novo), abre "Privacidade e dados"
 * e sair. Quem não é membro da organização aberta (a plataforma dentro de outra igreja)
 * não tem "Meu perfil" nem agenda nela.
 */
export default function UserMenu({ onLogout }: { onLogout: () => void }) {
  const { me, email, persona, guest } = useStore();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { openCalendar, openInstall, openPrivacy } = useOverlays();
  const { installed } = usePwa();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Menu do usuário"
        className="flex h-11 items-center gap-2.5 rounded-control px-1.5 text-left hover:bg-header-hover"
      >
        <Avatar name={me.name} photo={me.photo} size="lg" />
        <span className="flex flex-col leading-tight max-xl:hidden">
          <span className="text-control font-semibold">{me.name}</span>
          <span className="text-caption text-ink-subtle">{PERSONA_LABEL[persona]}</span>
        </span>
        <ChevronDown aria-hidden className="size-4 stroke-[1.8] text-ink-subtle" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <div className="px-2.5 pt-2 pb-2.5">
          <p className="m-0 text-control font-semibold">{me.name}</p>
          <p className="m-0 text-caption text-ink-subtle">{email}</p>
        </div>
        {!guest ? (
          <>
            <DropdownMenuItem onSelect={() => navigate(paths.me)}>
              <User aria-hidden />
              Meu perfil
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate(`${paths.me}?aba=acesso`)}>
              <Lock aria-hidden />
              Conta e senha
            </DropdownMenuItem>
          </>
        ) : null}
        <DropdownMenuSeparator />
        {!guest ? (
          <DropdownMenuItem onSelect={openCalendar}>
            <CalendarDays aria-hidden />
            Sincronizar agenda
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={openInstall}>
          {installed ? <CircleCheck aria-hidden /> : <Download aria-hidden />}
          {installed ? 'Aplicativo instalado' : 'Instalar aplicativo'}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={openPrivacy}>
          <ShieldCheck aria-hidden />
          Privacidade e dados
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={toggle}>
          {theme === 'dark' ? <Sun aria-hidden /> : <Moon aria-hidden />}
          {THEME_COPY[theme].item}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onLogout}>
          <LogOut aria-hidden />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
